import { useCallback, type CSSProperties } from "react";

import { colors } from "@skilitics/designer-theme";
import {
  BackButton,
  CancelButton,
  CompleteButton,
  NextButton,
  Wizard,
  type ModalDialogProps,
  type WizardProps,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useCreateModuleSettings } from "./CreateModuleSettingsContext";
import { BriefAssistantPage, InformAssistantPage, ModuleDetailsPage, SummaryPage } from "./pages";

type Props = ModalDialogProps &
  Pick<WizardProps, "onCancel" | "onComplete" | "contentStyle" | "stepperStyle" | "errorMessage"> & {
    busy: boolean;
  };

const buttonStyle: CSSProperties = { borderRadius: "8px" };

export const CreateModuleWizard = ({ busy, onCancel, ...rest }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const {
    metadata: { title },
    utilizeAi,
    reset,
  } = useCreateModuleSettings();

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  return (
    <Wizard
      {...rest}
      pages={[
        {
          id: "module-details",
          content: <ModuleDetailsPage />,
          stepper: {
            back: <CancelButton style={buttonStyle} onClick={reset} />,
            next: (
              <CompleteButton
                style={buttonStyle}
                busy={busy}
                label={formatMessage(utilizeAi ? "next" : busy ? "creating" : "create")}
                loadingProps={{ fillColor: colors.uiGrey["400"] }}
              />
            ),
            useBackAsCancel: true,
            advanceCondition: () => !!title,
          },
        },
        {
          id: "brief-assistant",
          content: <BriefAssistantPage />,
          stepper: {
            back: <BackButton style={buttonStyle} />,
            next: <NextButton style={buttonStyle} />,
          },
          showCondition: () => utilizeAi,
        },
        {
          id: "inform-assistant",
          content: <InformAssistantPage />,
          stepper: {
            back: <BackButton style={buttonStyle} />,
            next: <NextButton style={buttonStyle} />,
          },
          showCondition: () => utilizeAi,
        },
        {
          id: "summary-page",
          content: <SummaryPage />,
          stepper: {
            back: <BackButton style={buttonStyle} />,
            next: (
              <CompleteButton busy={busy} label={formatMessage(busy ? "creating" : "create")} style={buttonStyle} />
            ),
          },
          showCondition: () => utilizeAi,
        },
      ]}
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
      stepPanel="horizontal"
      onCancel={handleCancel}
    />
  );
};

const formatStrings = {
  create: {
    en: "Create Module",
  },
  creating: {
    en: "Creating...",
  },
  next: {
    en: "Next",
  },
} satisfies FormatMessages;
